import { ajax } from "lib/observable";
import { Observable } from "rxjs";
import "rxjs/add/operator/map";
import _ from "lodash";
import { WORKING_ORDER } from "constants/action-types";
import siteConfig from "site-config";
import { add as snackbarAdd } from "../../actions/snackbars";
import { formatMessage } from "../../lib/globalize-util";
import { WorkingProductsMeta, WorkingProductsRequestOptions } from "types/working-order/product";
import { getOrder } from "components/build-order";
import { stringify } from "querystring";
import { loadWorkingOrder, updateWorkingOrderState } from "actions/working-order-state";
import { forkJoin } from "rxjs/observable/forkJoin";
import { hideLoader } from "actions/loader";

export const fetchWorkingProductsEpic = (action$, store) =>
    action$
        .ofType(WORKING_ORDER.PRODUCTS.FETCH.START)
        .mergeMap(({ payload, meta = {} }: { payload: WorkingProductsRequestOptions; meta: WorkingProductsMeta }) => {
            const { cart, scope, working } = store.getState();
            const order = getOrder(cart);

            const fetchOptions = _.omitBy(
                {
                    __company: payload.company ?? order?.retailer?.ref,
                    __force_view_disabled: payload.viewDisabled ?? true,
                    __populate: scope.brand.getPreference("group_products") ? ["__grouped"] : [],
                    __effective_date: payload.effectiveDate ? new Date(payload.effectiveDate).valueOf() : null,
                    // TODO: template should be picked directly from the WO company when [NU-64106] will be complete!
                    __active_pricesheet: payload.activePricesheet ?? order?.__pricing_template ?? "",
                    ...(scope.brand.getPreference("set_warehouse_for_linesheets") &&
                        order?.selected_warehouse && { __warehouses: [order.selected_warehouse] }),
                },
                _.isUndefined
            );

            return forkJoin(
                Observable.from(_.chunk(payload.ids, 150))
                    .mergeMap(
                        (list) =>
                            ajax({
                                url: `${siteConfig.api}/api/v3.1/working/products?${stringify({ ...fetchOptions, _id: list })}`,
                            }),
                        10
                    )
                    .toArray()
            )
                .mergeMap((result) => {
                    const products = _.compact(_.flatMap(_.flatten(result), "response"));

                    const pick = (x) => !x?.__isVariant && x;

                    const mergedProducts = _.map(
                        products,
                        (product) => pick(product) || pick(working?.products?.payload?.[product._id]) || product
                    );

                    return meta?.omitStoreUpdate
                        ? [
                              { type: WORKING_ORDER.PRODUCTS.FETCH.COMPLETE },
                              updateWorkingOrderState({ products: mergedProducts }, meta),
                          ]
                        : [
                              {
                                  type: WORKING_ORDER.PRODUCTS.FETCH.SUCCESS,
                                  payload: mergedProducts,
                                  meta,
                              },
                          ];
                })
                .catch((error) =>
                    Observable.merge(
                        Observable.of({
                            error: _.pick(error, ["status", "message"]),
                            meta,
                            type: WORKING_ORDER.PRODUCTS.FETCH.ERROR,
                        }),
                        Observable.of(
                            snackbarAdd({ message: formatMessage("There was an error fetching your products") })
                        )
                    )
                );
        }, 1);

// Todo: This is a temp solution to hide loader while fetching /working-products.
export const workingProductsFetchCompleteEpic = (action$) =>
    action$
        .ofType(
            WORKING_ORDER.PRODUCTS.FETCH.SUCCESS,
            WORKING_ORDER.PRODUCTS.FETCH.COMPLETE,
            WORKING_ORDER.PRODUCTS.FETCH.ERROR
        )
        .filter((action) => action?.meta?.loaderReleaseMessage)
        .mergeMap((action) => [hideLoader(action.meta?.loaderReleaseMessage)]); // Pick 'loaderReleaseMessage' and dismiss loader dialog.

export const workingProductsFetchSuccessEpic = (action$) =>
    action$
        .ofType(WORKING_ORDER.PRODUCTS.FETCH.SUCCESS)
        .mergeMap(({ payload, meta }) =>
            _.compact([
                meta?.loadCart && loadWorkingOrder(meta.loadCart),
                meta?.notifyCart && updateWorkingOrderState({ products: payload }, meta),
            ])
        );
